'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { fetchUnreadCount } from '@/lib/notifications-api'

const POLL_MS = 30_000

interface NotificationInboxContextValue {
  unreadCount: number | null
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

const NotificationInboxContext = createContext<NotificationInboxContextValue | undefined>(
  undefined
)

export function NotificationInboxProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const [unreadCount, setUnreadCount] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setUnreadCount(null)
      setError(null)
      return
    }
    setLoading(true)
    try {
      const { unreadCount: n } = await fetchUnreadCount()
      setUnreadCount(n)
      setError(null)
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'خطا در بروزرسانی اعلان‌ها'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(null)
      setError(null)
      return
    }
    void refresh()
    const id = setInterval(() => void refresh(), POLL_MS)
    const onVis = () => {
      if (document.visibilityState === 'visible') void refresh()
    }
    document.addEventListener('visibilitychange', onVis)
    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [isAuthenticated, refresh])

  const value = useMemo(
    () => ({ unreadCount, loading, error, refresh }),
    [unreadCount, loading, error, refresh]
  )

  return (
    <NotificationInboxContext.Provider value={value}>{children}</NotificationInboxContext.Provider>
  )
}

export function useNotificationInbox(): NotificationInboxContextValue {
  const ctx = useContext(NotificationInboxContext)
  if (!ctx) {
    throw new Error('useNotificationInbox must be used within NotificationInboxProvider')
  }
  return ctx
}
